import { list, named, richtext, text, toggle, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { look, onPhone, paint, space, stack, typeface } from './styleTokens';

// The three ways words reach a page. All of them are leaves: nothing is ever dropped inside a heading,
// which is what lets the drag layer treat text as a single target rather than a box with an inside.
// Each lands bound to the store's own type and colour tokens, so repainting the shop repaints the copy.

// Describes the reading direction every text element offers: detected from the words, or pinned by hand.
// It is not a style property, so nothing else owns it — and an Arabic line inside a French page needs it.
const DIRECTIONS: readonly Choice[] = [
  ['auto', 'Detect from the words'], ['ltr', 'Left to right'], ['rtl', 'Right to left']
];

const directionField = () => named('direction', 'Text direction', DIRECTIONS);

/** The six levels, spelled as the six chips LightFunnels draws them as; H1 is the page's one title. */
const LEVELS: readonly Choice[] = [['h1', 'H1'], ['h2', 'H2'], ['h3', 'H3'], ['h4', 'H4'], ['h5', 'H5'], ['h6', 'H6']];

/** The marks a list may be led by, shown as the mark itself wherever one can be written out. */
const MARKERS: readonly Choice[] = [
  ['disc', 'Bullet'], ['circle', 'Hollow bullet'], ['square', 'Square'], ['number', '1.  2.  3.'],
  ['lower-alpha', 'a.  b.  c.'], ['lower-roman', 'i.  ii.  iii.'], ['tick', 'Tick'], ['none', 'No mark']
];

/** Whether the mark hangs outside the line of text or sits within it. */
const MARKER_PLACES: readonly Choice[] = [['outside', 'Outside the text'], ['inside', 'Inside the text']];

/** A title. Its level is what a screen reader and a search engine read; its size is a style. */
export const headingElement = element('heading', {
  label: 'Heading',
  description: 'A title. Choose the level for search engines and screen readers; set the size in Styles.',
  category: 'Texts',
  icon: 'heading',
  props: [
    // LightFunnels edits a title through a rich-text box, not a single line: bold, a colour and a link
    // inside the words are what a headline is written with, and a plain input cannot hold any of them.
    richtext('text', 'Content'), named('level', 'Level', LEVELS), directionField()
  ],
  defaults: {
    props: { text: 'Heading 1', level: 'h1', direction: 'auto' },
    styles: onPhone(
      look({
        fontFamily: typeface('heading'), fontSize: '30px', fontWeight: typeface('headingWeight'),
        lineHeight: 1.3, color: paint('heading'), marginBottom: space('sm')
      }),
      { fontSize: '22px' }
    )
  }
});

/**
 * A block of running copy — the element a page has more of than any other.
 *
 * Now checked against the real thing: LightFunnels edits its Text element through a rich-text Content
 * box — bold, italic, underline, strike, a colour and a link — not a plain textarea. Ours had the
 * textarea only because the parity capture for this element caught `product-description` instead, so a
 * merchant could not bold a word in the one element a page has most of. Copy stored before this is
 * still plain words, and `ParagraphElement` still draws it that way.
 */
export const paragraphElement = element('paragraph', {
  label: 'Paragraph',
  description: 'A block of running text. Blank lines between sentences become separate paragraphs.',
  category: 'Texts',
  icon: 'pilcrow',
  // Fill the width sets the words on one line across the whole box, the way a theme sets the store's name in its footer.
  props: [richtext('text', 'Content'), directionField(), toggle('fitWidth', 'Fill the width on one line')],
  defaults: {
    props: { text: 'Say in a sentence or two what this product does for the person reading.', direction: 'auto' },
    styles: look({
      fontFamily: typeface('body'), fontSize: '15px', lineHeight: 1.6,
      color: paint('text'), marginBottom: space('sm')
    })
  }
});

/** A bulleted, numbered or ticked list — the shape a benefit reads best in. */
export const listElement = element('list', {
  label: 'List',
  description: 'Bulleted, numbered or ticked lines — the shape a list of benefits reads best in.',
  category: 'Texts',
  icon: 'list',
  props: [
    // The lines come first because they are what the element says; LightFunnels keeps its own two lines
    // as child nodes and files the marker under Styles, so ours reads content-then-appearance the same way.
    list('items', 'Lines', [text('text', 'Line')]),
    named('marker', 'Style', MARKERS),
    named('markerPosition', 'Position', MARKER_PLACES), directionField()
  ],
  defaults: {
    props: {
      marker: 'disc',
      markerPosition: 'outside',
      direction: 'auto',
      items: [{ text: 'List item 01' }, { text: 'List item 02' }]
    },
    styles: look({ ...stack('8px'), fontFamily: typeface('body'), fontSize: '15px', lineHeight: 1.6, color: paint('text') })
  }
});
